import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Calendar, User, ArrowLeft, ArrowRight } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import Seo, { ORG_SCHEMA, breadcrumbSchema } from "@/components/site/Seo";
import { publicApi } from "@/lib/crm";
import { BRAND } from "@/lib/constants";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

export default function InsightArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null); setNotFound(false);
    publicApi.getInsight(slug)
      .then(setArticle)
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="bg-white min-h-[70vh] pt-40 pb-24">
        <Seo title="Article Not Found" description="This article could not be found." path={`/insights/${slug}`} noindex />
        <div className="mx-auto max-w-2xl px-6 text-center">
          <h1 className="font-serif text-4xl">Article not found</h1>
          <p className="mt-4 text-black/60">The briefing you're looking for may have been removed.</p>
          <Link to="/insights" className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-overline uppercase underline underline-offset-4">
            <ArrowLeft size={14}/> Back to all insights
          </Link>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="bg-white min-h-[70vh] pt-40 pb-24">
        <div className="mx-auto max-w-3xl px-6 animate-pulse text-black/30">Loading briefing…</div>
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `https://velor.ae/insights/${article.slug}#article`,
    headline: article.title,
    description: article.excerpt || article.seo_description || "",
    image: article.cover_image_url ? [article.cover_image_url] : undefined,
    datePublished: article.published_at,
    dateModified: article.updated_at || article.published_at,
    author: { "@type": "Person", name: article.author_name },
    publisher: {
      "@id": "https://velor.ae/#organization",
      "@type": "Organization",
      name: "Velor Advisory",
      logo: { "@type": "ImageObject", url: "https://velor.ae/favicon.png" },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://velor.ae/insights/${article.slug}`,
    },
    articleSection: article.category || undefined,
    inLanguage: "en-GB",
  };

  return (
    <div className="bg-white">
      <Seo
        title={(article.seo_title || article.title).slice(0, 60)}
        description={(article.seo_description || article.excerpt || article.title).slice(0, 160)}
        focusKeyword={article.focus_keyword}
        path={`/insights/${article.slug}`}
        image={article.cover_image_url || undefined}
        schema={[
          ORG_SCHEMA,
          articleSchema,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Insights", path: "/insights" },
            { name: article.title, path: `/insights/${article.slug}` },
          ]),
        ]}
      />

      {/* HERO */}
      <section className="relative pt-28 md:pt-32" data-testid="article-hero">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10 pt-16 md:pt-24 pb-10">
          <Link to="/insights" data-testid="back-to-insights" className="inline-flex items-center gap-2 text-[11px] tracking-overline uppercase text-black/50 hover:text-black transition">
            <ArrowLeft size={14}/> Back to Insights
          </Link>
          {article.category && (
            <div className="mt-10 text-[11px] tracking-overline uppercase text-black/45">{article.category}</div>
          )}
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight max-w-4xl">
            {article.title}
          </h1>
          {article.excerpt && (
            <p className="mt-8 text-lg text-black/65 font-light leading-relaxed max-w-2xl">{article.excerpt}</p>
          )}
          <div className="mt-10 flex flex-wrap items-center gap-6 text-[11px] tracking-overline uppercase text-black/45">
            <span className="inline-flex items-center gap-2"><User size={12}/> {article.author_name}</span>
            {article.published_at && (
              <span className="inline-flex items-center gap-2"><Calendar size={12}/> {formatDate(article.published_at)}</span>
            )}
            {article.reading_time && <span>{article.reading_time} min read</span>}
          </div>
        </div>

        {article.hero_image_url && (
          <div className="mx-auto max-w-[1400px] px-0 md:px-6 lg:px-10">
            <div className="aspect-[16/9] w-full overflow-hidden">
              <img
                src={article.hero_image_url}
                alt={article.title}
                fetchPriority="high"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}
      </section>

      {/* CONTENT */}
      <section className="py-16 md:py-24" data-testid="article-content">
        <div className="mx-auto max-w-[760px] px-6">
          <Reveal>
            <article className="article-prose">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {article.content_markdown || ""}
              </ReactMarkdown>
            </article>
          </Reveal>

          {/* CTA */}
          <div className="mt-20 border-t border-black/10 pt-12">
            <div className="text-[11px] tracking-overline uppercase text-black/45">Next step</div>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl">Speak privately with an advisor.</h2>
            <p className="mt-4 text-black/65 font-light leading-relaxed max-w-xl">
              A 30-minute confidential consultation with a senior advisor. No commitment, no pressure.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={BRAND.calendly} target="_blank" rel="noreferrer" data-testid="article-cta-book" className="inline-flex items-center gap-2 px-7 py-4 bg-black text-white text-[12px] tracking-overline uppercase hover:bg-black/85 transition">
                Book a consultation <ArrowRight size={14}/>
              </a>
              <Link to="/insights" data-testid="article-more" className="inline-flex items-center gap-2 px-7 py-4 border border-black/25 text-[12px] tracking-overline uppercase hover:bg-black hover:text-white transition">
                More briefings
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
